import { Grid } from '@mui/material';
import { CustomTabPanel } from '../CustomPanel';
import DateRangeDiv from '../HeaderMenu/DateRangeDiv';
import { ThemePalette } from 'mui.theme';
import HaulerProductionDetails from './component/HaulerProductionDetails';
import type {
  productionLoadingStates,
  fleetData,
  productionAsset,
} from 'types/production.types';
import HaulerAnalyticsWrapper from './component/HaulerAnalyticsWrapper';
import AverageCycleTime from '../AllFleet/components/AverageCycleTime';
import {
  getHaulerSummary,
  getProductionAssetsByTypeMapping,
} from '../AllFleet/utils';
import FleetProductionChart from '../AllFleet/components/FleetProductionChart';
import AssetView from './component/HaulerAssetView';
import Suggestions from '../components/Suggestions/Suggestions';
import HaulerFuelConsumption from './component/FuelConsumption';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  value: number;
  enableDarkTheme: boolean;
  isSelected: string;
  setIsSelected: any;
  handleDateChange: any;
  startDate: any;
  setStartDate: any;
  endDate: any;
  setEndDate: any;
  yesterday: any;
  unitSystem: string;
  customerCode: string;
  loadingStates: productionLoadingStates;
  assetOverview: any;
  data: fleetData;
  aggregation: string;
  setAggregation: any;
  isPrinting: boolean;
  setDetailsVal: any;
  setAnalyticsData: any;
  setHoursBreakDown: any;
  setUserSelections: any;
  setSuggestionData: any;
  selectedChartSource: any;
  setSelectedChartSource: any;
  rollingDailyAverage: number;
  shiftWiseAnalysis: boolean;
  setShiftWiseAnalysis: any;
  shiftNumber: number;
  setShiftNumber: any;
  handleFetchCustomDateRangeData: ({
    aggregation,
  }: {
    aggregation: string;
  }) => void;
  handleFetchCustomOnAggregationChange: ({
    aggregation,
    startDate,
    endDate,
  }: {
    aggregation: string;
    startDate: Date;
    endDate: Date;
  }) => void;
  // currentDailyAverage: number;
}

const Hauler = ({
  value,
  enableDarkTheme,
  isSelected,
  setIsSelected,
  handleDateChange,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  yesterday,
  unitSystem,
  customerCode,
  loadingStates,
  data,
  assetOverview,
  aggregation,
  setAggregation,
  isPrinting,
  setDetailsVal,
  setAnalyticsData,
  setHoursBreakDown,
  setUserSelections,
  setSuggestionData,
  selectedChartSource,
  setSelectedChartSource,
  rollingDailyAverage,
  shiftWiseAnalysis,
  setShiftWiseAnalysis,
  shiftNumber,
  setShiftNumber,
  handleFetchCustomDateRangeData,
  handleFetchCustomOnAggregationChange,
}: // currentDailyAverage,
Props) => {
  const { t } = useTranslation();
  const haulersSummary: any = getHaulerSummary(data.summary, t);

  const haulerAssets: productionAsset[] = getProductionAssetsByTypeMapping(
    data.assets,
    'Haul Truck'
  );

  const haulerConsumptionData = {
    loaded: data.summary.haul_truck_loaded_trip_fuel_consumption_l,
    unloaded: data.summary.haul_truck_unloaded_trip_fuel_consumption_l,
    loading: data.summary.haul_truck_loading_fuel_consumption_l,
    unloading: data.summary.haul_truck_unloading_fuel_consumption_l,
  };

  const currentDailyAverage = useMemo(() => {
    // if the chartData is empty, return 0, else extract seven day working minutes and return the average
    if (data?.fleetPeriodSummary?.length > 0) {
      const sevenDayDataObject =
        data?.fleetPeriodSummary?.find(
          (dailyProductionObject) =>
            dailyProductionObject?.time_range === 'hauler_last_7_days'
        )?.daily_production ?? {};

      const sevenDayData = Object.values(sevenDayDataObject ?? {}).map(
        (value) => +value
      );

      const sevenDayTotalWorkingMinutes = sevenDayData.reduce(
        (acc: number, curr: number) => acc + curr,
        0
      );

      return sevenDayTotalWorkingMinutes / sevenDayData?.length;
    } else {
      return 0;
    }
  }, [data?.fleetPeriodSummary]);

  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  return (
    <>
      <CustomTabPanel value={value} index={1}>
        <div>
          <Grid
            container
            spacing={2}
            justifyContent="space-between"
            sx={{
              color: palette.textPrimary,
            }}
          >
            <HaulerProductionDetails
              enableDarkTheme={enableDarkTheme}
              isLoading={loadingStates.isLoadingFleetDetails}
              unitSystem={unitSystem}
              data={data.totalFleetDetails}
              rollingDailyAverage={rollingDailyAverage}
              currentDailyAverage={currentDailyAverage ?? 0}
              isLoadingFleetPeriodSummary={
                loadingStates?.isLoadingFleetPeriodSummary
              }
              setDetailsVal={setDetailsVal}
            />

            <FleetProductionChart
              enableDarkTheme={enableDarkTheme}
              data={data.fleetPeriodSummary}
              isLoading={loadingStates.isLoadingFleetPeriodSummary}
              unitSystem={unitSystem}
              title={t('daily_haulers_production')}
              // setRollingDailyAverage={setRollingDailyAverage}
              setUserSelections={setUserSelections}
              selectedChartSource={selectedChartSource}
              setSelectedChartSource={setSelectedChartSource}
            />
          </Grid>

          <DateRangeDiv
            enableDarkTheme={enableDarkTheme}
            isSelected={isSelected}
            setIsSelected={setIsSelected}
            handleDateChange={handleDateChange}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            yesterday={yesterday}
            ignoreYtd={value === 2}
            aggregation={aggregation}
            setAggregation={setAggregation}
            shiftWiseAnalysis={shiftWiseAnalysis}
            setShiftWiseAnalysis={setShiftWiseAnalysis}
            shiftNumber={shiftNumber}
            setShiftNumber={setShiftNumber}
            handleFetchCustomDateRangeData={handleFetchCustomDateRangeData}
            handleFetchCustomOnAggregationChange={
              handleFetchCustomOnAggregationChange
            }
          />

          <HaulerAnalyticsWrapper
            enableDarkTheme={enableDarkTheme}
            data={data.summary}
            isLoading={loadingStates.isLoadingFleetSummary}
            unitSystem={unitSystem}
            assetOverview={assetOverview}
            scoreData={data.score}
            chartData={data.summaryChart}
            aggregation={aggregation}
            isSelected={isSelected}
            customerCode={customerCode}
          />

          <Grid
            container
            spacing={2}
            justifyContent="space-between"
            sx={{
              paddingTop: '15px',
              color: palette.textPrimary,
            }}
          >
            <Grid item lg={9} sm={12} md={12}>
              <Grid container spacing={2} justifyContent="space-between">
                <AverageCycleTime
                  enableDarkTheme={enableDarkTheme}
                  title={t('average_hauler_cycle_time')}
                  data={haulersSummary}
                  isLoading={loadingStates.isLoadingFleetSummary}
                  setHoursBreakDown={(value: any) =>
                    setHoursBreakDown((prev: any) => ({
                      ...prev,
                      hauler: value,
                    }))
                  }
                />
                <HaulerFuelConsumption
                  enableDarkTheme={enableDarkTheme}
                  title={t('fuel_consumption')}
                  isLoading={loadingStates.isLoadingFleetSummary}
                  unitSystem={unitSystem}
                  data={haulerConsumptionData}
                  setHoursBreakDown={(value: any) =>
                    setHoursBreakDown((prev: any) => ({
                      ...prev,
                      'Fuel Consumption': value,
                    }))
                  }
                />
              </Grid>
            </Grid>
            <Suggestions
              enableDarkTheme={enableDarkTheme}
              minHeight="300px"
              data={data.suggestions}
              isLoading={loadingStates.isLoadingSuggestions}
              startDate={startDate}
              isSelected={isSelected}
              setSuggestionData={setSuggestionData}
            />
          </Grid>

          <AssetView
            isLoading={loadingStates.isLoadingFleetAssets}
            data={haulerAssets}
            isSelected={isSelected}
            aggregation={aggregation}
          />
        </div>
      </CustomTabPanel>
    </>
  );
};

export default Hauler;
